import React, { useEffect, useMemo, useState } from 'react';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import JournalView from '@/pages/journal/JournalView';
import KindergartenView from '@/pages/journal/KindergartenView';

/**
 * Электронный дневник для учителя.
 *
 * UX:
 *   • Сверху селект «Класс» — список тех, где учитель ведёт хотя бы один
 *     предмет или является классруком/ассистентом.
 *   • Селект «Предмет» — предметы выбранного класса. У каждого пометка
 *     роли: «ведёте» (canGrade) или «просмотр» (классрук).
 *   • Сам журнал — переиспользуем `JournalView`. `canEdit` передаём
 *     равным `canGrade` выбранного предмета: учитель видит чужие предметы
 *     read-only, свои — с правом ставить оценки и темы.
 *
 * Учебный год не выбираем — он автоматически определяется выбранным
 * классом (как у админа в `/classes/:id`).
 */
const URL_SCHEMA = { class_id: '', subject_id: '' };

export default function TeacherJournalPage() {
  const { tweaks, updateTweak } = useShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);

  const [items, setItems]   = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    setLoad(true); setError(null);
    api.get('/api/teacher/journal-classes')
      .then((res) => {
        const list = res.items || [];
        setItems(list);
        // Дефолты: первый «активный» класс + первый предмет, который
        // учитель ведёт (или просто первый, если своих нет).
        if (!params.class_id && list.length) {
          const def = list.find((c) => !c.archive) || list[0];
          const sub = (def.subjects || []).find((s) => s.canGrade)
                   || (def.subjects || [])[0];
          const patch = { class_id: String(def.id) };
          if (sub) patch.subject_id = String(sub.id);
          setParams(patch);
        }
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeClass = useMemo(
    () => items.find((c) => c.id === Number(params.class_id)) || null,
    [items, params.class_id]
  );
  // Детсадовская группа: журнал не по предметам, а посещаемость. Воспитатель
  // (классрук/ассистент) отмечает явку; право на запись даёт сам факт того,
  // что группа его (роль teacher/assistant в списке journal-classes) — бэк
  // это проверяет повторно (requireKgAccess).
  const isKg = (activeClass?.stage || '') === 'kindergarten';
  const kgCanWrite = isKg && ['teacher', 'assistant'].includes(activeClass?.role || '');

  const subjectsInClass = activeClass?.subjects || [];
  const activeSubject = useMemo(
    () => subjectsInClass.find((s) => s.id === Number(params.subject_id)) || null,
    [subjectsInClass, params.subject_id]
  );

  const onClassChange = (e) => {
    const cid = e.target.value;
    const cls = items.find((c) => c.id === Number(cid));
    // Для детсада предмет не нужен — сбрасываем subject_id.
    if ((cls?.stage || '') === 'kindergarten') {
      setParams({ class_id: cid, subject_id: '' });
      return;
    }
    const sub = (cls?.subjects || []).find((s) => s.canGrade)
             || (cls?.subjects || [])[0];
    setParams({ class_id: cid, subject_id: sub ? String(sub.id) : '' });
  };

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Электронный дневник']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />

      <div className="jr-pickers">
        <select
          value={params.class_id || ''}
          onChange={onClassChange}
          className="jr-pickers__class"
        >
          <option value="">— класс —</option>
          {items.map((c) => (
            <option key={c.id} value={c.id}>
              {c.code}{c.name && c.name !== c.code ? ' · ' + c.name : ''}
              {c.yearName ? ' (' + c.yearName + ')' : ''}
              {c.archive ? ' · архив' : ''}
              {c.role === 'teacher' ? ' · классрук' : c.role === 'assistant' ? ' · ассистент' : ''}
            </option>
          ))}
        </select>

        {/* Селект предмета — только для школьных классов. У детсада
            предметов нет: там сразу сетка посещаемости. */}
        {!isKg && (
          <select
            value={params.subject_id || ''}
            onChange={(e) => setParams({ subject_id: e.target.value })}
            className="jr-pickers__class"
            disabled={!activeClass || subjectsInClass.length === 0}
          >
            <option value="">— предмет —</option>
            {subjectsInClass.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}{s.canGrade ? ' · ведёте' : ' · просмотр'}
              </option>
            ))}
          </select>
        )}

        {!isKg && activeSubject && !activeSubject.canGrade && (
          <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            Этот предмет ведёт другой учитель — вы можете только смотреть.
          </span>
        )}
      </div>

      {loading ? (
        <div className="ref-empty">Загрузка…</div>
      ) : error ? (
        <div className="ref-empty" style={{ color: 'var(--red-fg)' }}>{error}</div>
      ) : items.length === 0 ? (
        <div className="ref-empty">
          У вас нет назначенных классов или уроков — обратитесь к администратору.
        </div>
      ) : !params.class_id ? (
        <div className="ref-empty">Выберите класс.</div>
      ) : isKg ? (
        <KindergartenView
          key={'kg:' + params.class_id}
          classId={Number(params.class_id)}
          canWrite={kgCanWrite}
        />
      ) : !params.subject_id ? (
        <div className="ref-empty">Выберите предмет.</div>
      ) : (
        <JournalView
          key={params.class_id + ':' + params.subject_id}
          classId={Number(params.class_id)}
          canEdit={!!activeSubject?.canGrade}
          hideSubjectPicker
        />
      )}
    </>
  );
}
